import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import { addReportToServer, answersToAnsString, secondsToHMS } from "../../functions/sendReport";
import { isDemoUser } from "../../utils/yogaResumeRoute";
import { apiUrl } from "../../utils/apiUrl";
import YogaThemedModal from "../../components/yoga/YogaThemedModal";
import pointsIcon from "../../img/points.png";
import howToPlayIcon from "../../img/how-to-play.png";
import timerIcon from "../../img/timer.png";
import "./game.css";

function appBaseUrl() {
  const b = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return b || (typeof window !== "undefined" ? window.location.origin : "");
}

function demoPlansUrl() {
  return `${appBaseUrl()}/plans`;
}

const DEMO_QUESTION_LIMIT = 3;

function normalizeColors(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.slice(0, 9).map((c, i) => ({
    id: String(c?.id || String(i + 1)),
    color_name: String(c?.color_name || "").trim(),
    color_code: String(c?.color_code || "#888888").trim(),
    color_value: String(c?.color_value || "").trim(),
    color_definitions: String(c?.color_definitions || "").trim(),
  }));
}

function normalizeQuestions(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((q, i) => {
    const answers = Array.isArray(q?.correct_answers)
      ? q.correct_answers.map((a) => String(a).trim()).filter(Boolean).slice(0, 3)
      : [];
    return {
      id: String(q?.id || `q${i + 1}`),
      text: String(q?.text || "").trim(),
      select_count: Math.min(3, Math.max(1, Number(q?.select_count) || answers.length || 1)),
      correct_answers: answers,
      why_these_answers_fit: String(q?.why_these_answers_fit || "").trim(),
    };
  });
}

function normalizeRules(raw) {
  if (Array.isArray(raw)) {
    return raw.map((r) => String(r ?? "").trim()).filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.keys(raw)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => String(raw[k] ?? "").trim())
      .filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      return normalizeRules(JSON.parse(raw));
    } catch {
      return [raw.trim()];
    }
  }
  return [];
}

function secondsToMS(sec) {
  const total = Math.max(0, Math.floor(Number(sec) || 0));
  const m = Math.floor(total / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(m)}:${pad(s)}`;
}

function shuffleCopy(list) {
  const next = [...list];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function pickPlayQuestions(themeData) {
  const all = normalizeQuestions(themeData?.questions);
  if (!all.length) return [];
  const available = all.length;
  const requested = parseInt(String(themeData?.display_question_count ?? "10"), 10);
  const limit = Math.min(
    available,
    Math.max(1, Number.isFinite(requested) && requested > 0 ? requested : Math.min(10, available))
  );
  const shuffle = String(themeData?.shuffle_questions ?? "false").trim().toLowerCase() === "true";
  const pool = shuffle ? shuffleCopy(all) : all;
  return pool.slice(0, limit);
}

function scoreSelection(selected, correctAnswers, correctPts, wrongPts) {
  const correctSet = new Set(correctAnswers);
  let delta = 0;
  const breakdown = [];
  for (const value of selected) {
    if (correctSet.has(value)) {
      delta += correctPts;
      breakdown.push({ value, ok: true, pts: correctPts });
    } else {
      delta -= wrongPts;
      breakdown.push({ value, ok: false, pts: -wrongPts });
    }
  }
  return { delta, breakdown };
}

export default function GamePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const colors = useMemo(() => normalizeColors(themeData?.colors), [themeData]);
  const playQuestionsRef = useRef(null);
  const questions = useMemo(() => {
    if (playQuestionsRef.current?.length) return playQuestionsRef.current;
    const picked = pickPlayQuestions(themeData);
    if (picked.length) playQuestionsRef.current = picked;
    return picked;
  }, [themeData]);

  const correctPts = Number(themeData?.correct_points) || 10;
  const wrongPts = Number(themeData?.wrong_points ?? themeData?.wromg_points) || 5;
  const playRules = useMemo(() => {
    const parsed = normalizeRules(themeData?.rules);
    if (parsed.length > 0) return parsed;
    return [
      "Read a situation. Pick exactly 2 or 3 values.",
      "Discover why they fit, then move to the next moment.",
      `+${correctPts} per correct colour`,
      `−${wrongPts} per incorrect colour`,
    ];
  }, [themeData, correctPts, wrongPts]);
  const showWhyAnswersFit =
    String(themeData?.show_why_answers_fit ?? "true").trim().toLowerCase() === "true";

  const titleColor = String(themeData?.text_color || "#ffffff").trim() || "#ffffff";
  const btnBg = themeButtonBackground(themeData) || "#3B82F6";
  const btnColor = themeButtonTextColor(themeData) || "#ffffff";
  const popupBg = String(themeData?.popup_background_color || "#1B1433").trim() || "#1B1433";
  const popupText = String(themeData?.popup_text_color || "#ffffff").trim() || "#ffffff";
  const btnStyle = { backgroundColor: btnBg, color: btnColor };
  const pageStyle = {
    color: titleColor,
    "--coc-text": titleColor,
    "--coc-btn-bg": btnBg,
    "--coc-btn-text": btnColor,
  };

  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState([]);
  const [score, setScore] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [answersLog, setAnswersLog] = useState([]);
  const [finished, setFinished] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [whyOpen, setWhyOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [alertModal, setAlertModal] = useState(null);
  const [rulesOpen, setRulesOpen] = useState(false);

  const question = questions[qIndex] || null;
  const selectCount = question?.select_count || 1;
  const atMax = selected.length >= selectCount;
  const progressCompleted = feedback ? qIndex + 1 : qIndex;
  const progressPct =
    questions.length > 0 ? Math.round((progressCompleted / questions.length) * 100) : 0;

  const closeAlert = useCallback(() => setAlertModal(null), []);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  useEffect(() => {
    if (finished) return undefined;
    const id = setInterval(() => setElapsedSec((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [finished]);

  const saveLocalGameRow = useCallback(
    async (finalScore, log) => {
      if (!user?.userId || !adminToken || isDemoUser(user)) return;
      try {
        await fetch(apiUrl("/spotSubmit"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            email: user.email || "",
            name: user.name || "",
            time: secondsToHMS(elapsedSec),
            points: finalScore,
            ans: answersToAnsString(log),
            currentTheme: themeData?.themename || themeData?.themeName,
          }),
        });
      } catch (err) {
        console.error("spotSubmit", err);
      }
    },
    [user, adminToken, elapsedSec, themeData]
  );

  const finishGame = useCallback(
    async (finalScore, log) => {
      if (user && !isDemoUser(user)) {
        addReportToServer(user, {
          points: finalScore,
          elapsedSeconds: elapsedSec,
          answers: log,
        });
        await saveLocalGameRow(finalScore, log);
        navigate("/feedback", { replace: true });
        return;
      }
      setFinished(true);
    },
    [user, elapsedSec, saveLocalGameRow, navigate]
  );

  const toggleColor = (value) => {
    if (!question || feedback) return;
    setSelected((prev) => {
      if (prev.includes(value)) return prev.filter((v) => v !== value);
      if (prev.length >= selectCount) return prev;
      return [...prev, value];
    });
  };

  const handleSubmit = () => {
    if (!question || submitting) return;
    if (selected.length !== selectCount) return;

    setSubmitting(true);
    const { delta, breakdown } = scoreSelection(
      selected,
      question.correct_answers,
      correctPts,
      wrongPts
    );
    const nextScore = score + delta;
    const logEntry = `Q${qIndex + 1}: ${selected.join(" + ")} (${delta >= 0 ? "+" : ""}${delta})`;
    const nextLog = [...answersLog, logEntry];

    setScore(nextScore);
    setAnswersLog(nextLog);
    const missed = (question.correct_answers || []).filter((a) => !selected.includes(a));
    setFeedback({
      delta,
      breakdown,
      missed,
      why: question.why_these_answers_fit || "",
      correctAnswers: [...(question.correct_answers || [])],
      totalScore: nextScore,
      answersLog: nextLog,
    });

    if (showWhyAnswersFit) {
      setWhyOpen(true);
    }

    setSubmitting(false);
  };

  const handleNext = () => {
    if (!feedback) return;
    const nextIndex = qIndex + 1;
    const finalScore = feedback.totalScore != null ? feedback.totalScore : score;
    const finalLog = Array.isArray(feedback.answersLog) ? feedback.answersLog : answersLog;
    setFeedback(null);
    setWhyOpen(false);
    setSelected([]);

    if (isDemoUser(user) && nextIndex >= DEMO_QUESTION_LIMIT) {
      setFinished(true);
      setAlertModal({
        title: "Thank you for playing.",
        message: "Subscribe to any PLAN to play with your peers.",
        success: true,
        confirmText: "OK",
        onConfirm: () => {
          closeAlert();
          window.location.href = demoPlansUrl();
        },
      });
      return;
    }

    if (nextIndex >= questions.length) {
      finishGame(finalScore, finalLog);
      return;
    }
    setQIndex(nextIndex);
  };

  if (status === "loading" || status === "idle") {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="coc-page" style={pageStyle}>
        <p className="coc-empty" style={{ color: titleColor }}>
          Could not load game data.{themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="coc-submit"
          style={btnStyle}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!colors.length || !questions.length) {
    return (
      <div className="coc-page" style={pageStyle}>
        <p className="coc-empty" style={{ color: titleColor }}>
          This game is not configured yet. An admin needs to add colours and questions.
        </p>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="coc-page" style={pageStyle}>
        {alertModal ? (
          <YogaThemedModal
            open
            title={alertModal.title}
            titleId="coc-alert-title"
            themeData={themeData}
            matchButtonColors
            confirmText={alertModal.confirmText || "OK"}
            onConfirm={alertModal.onConfirm || closeAlert}
          >
            <p>{alertModal.message}</p>
          </YogaThemedModal>
        ) : (
          <div className="quiz-loader-container">
            <div className="quiz-loader" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="coc-page" style={pageStyle}>
      <div className="coc-hud-wrap">
        <div className="coc-hud" style={{ color: titleColor }}>
          <span className="coc-hud-situation" style={{ color: titleColor }}>
            <img src={timerIcon} alt="" className="coc-hud-icon" />
            {secondsToMS(elapsedSec)}
          </span>
          <span className="coc-hud-right">
            <span className="coc-hud-score" style={{ color: titleColor }}>
              <img src={pointsIcon} alt="" className="coc-hud-icon" />
              {score}
            </span>
            <button
              type="button"
              className="coc-hud-rules"
              aria-label="How to play"
              onClick={() => setRulesOpen(true)}
            >
              <img src={howToPlayIcon} alt="" />
            </button>
          </span>
        </div>
        <div className="coc-progress coc-progress--hud" aria-label="Game progress">
          <div className="coc-progress-meta" style={{ color: titleColor }}>
            <span>
              Progress {progressCompleted} of {questions.length}
            </span>
            <span>{progressPct}%</span>
          </div>
          <div className="coc-progress-track">
            <div
              className="coc-progress-fill"
              style={{ width: `${progressPct}%`, backgroundColor: btnBg }}
            />
          </div>
        </div>
      </div>

      <div className="coc-q-stage" key={qIndex}>
      <div className="coc-question-card">
        <p className="coc-question-eyebrow" style={{ color: titleColor }}>
          What would you choose?
        </p>
        <p className="coc-question-text" style={{ color: titleColor }}>
          {question.text}
        </p>
      </div>

      <div className="coc-select-meta" style={{ color: titleColor }}>
        <span style={{ color: titleColor }}>
          Choose {selectCount} colour{selectCount === 1 ? "" : "s"}
        </span>
        <span style={{ color: titleColor }}>
          {selected.length} of {selectCount} selected
        </span>
      </div>

      <div className="coc-grid">
        {colors.map((c, i) => {
          const on = selected.includes(c.color_value);
          const disabled = Boolean(feedback) || (!on && atMax);
          return (
            <button
              key={c.id}
              type="button"
              className={`coc-color-card${on ? " coc-color-card--on" : ""}${
                disabled && !on ? " coc-color-card--disabled" : ""
              }`}
              style={{
                color: titleColor,
                WebkitTextFillColor: titleColor,
                ...(on
                  ? {
                      borderColor: btnBg,
                      boxShadow: `0 0 0 3px ${btnBg}, 0 10px 24px rgba(0, 0, 0, 0.28)`,
                    }
                  : null),
              }}
              onClick={() => toggleColor(c.color_value)}
              disabled={disabled && !on}
              aria-pressed={on}
            >
              <span className="coc-color-row">
                <span className="coc-color-dot" style={{ background: c.color_code }}>
                  {i + 1}
                </span>
                <span className="coc-color-name" style={{ color: titleColor }}>
                  {c.color_name}
                </span>
              </span>
              <span className="coc-color-value" style={{ color: titleColor }}>
                {c.color_value}
              </span>
            </button>
          );
        })}
      </div>
      </div>

      {feedback && !showWhyAnswersFit ? (
        <div className="coc-after-submit">
          <button type="button" className="coc-submit" style={btnStyle} onClick={handleNext}>
            {qIndex + 1 >= questions.length ? "See results →" : "Next situation →"}
          </button>
        </div>
      ) : !feedback ? (
        <button
          type="button"
          className="coc-submit"
          style={btnStyle}
          onClick={handleSubmit}
          disabled={selected.length !== selectCount || submitting}
        >
          Submit colours →
        </button>
      ) : null}

      {whyOpen && feedback ? (
        <div className="coc-fit-overlay" role="presentation">
          <div
            className="coc-fit-modal"
            role="dialog"
            aria-labelledby="coc-fit-title"
            style={{
              backgroundColor: popupBg,
              color: popupText,
              "--coc-fit-text": popupText,
            }}
          >
            <div className="coc-fit-header">
              <h2 id="coc-fit-title" className="coc-fit-title" style={{ color: popupText }}>
                Here&apos;s how the values fit
              </h2>
              <span
                className={`coc-fit-total${feedback.delta >= 0 ? " coc-fit-total--pos" : " coc-fit-total--neg"}`}
              >
                This round {feedback.delta >= 0 ? "+" : ""}
                {feedback.delta}
              </span>
            </div>
            <p className="coc-fit-running-total" style={{ color: popupText }}>
              Total score: {feedback.totalScore != null ? feedback.totalScore : score}
            </p>

            <ul className="coc-fit-list">
              {(feedback.breakdown || []).map((b) => (
                <li key={`sel-${b.value}`} className="coc-fit-row" style={{ borderColor: `${popupText}33` }}>
                  <span style={{ color: popupText }}>
                    {b.ok ? "✓" : "×"} {b.value}
                  </span>
                  <span style={{ color: popupText }}>
                    {b.pts >= 0 ? "+" : ""}
                    {b.pts}
                  </span>
                </li>
              ))}
              {(feedback.missed || []).map((value) => (
                <li key={`miss-${value}`} className="coc-fit-row" style={{ borderColor: `${popupText}33` }}>
                  <span style={{ color: popupText }}>Missed: {value}</span>
                  <span style={{ color: popupText }}>0</span>
                </li>
              ))}
            </ul>

            {feedback.why ? (
              <p className="coc-fit-why" style={{ color: popupText }}>
                {feedback.why}
              </p>
            ) : null}

            <p className="coc-fit-note" style={{ color: popupText }}>
              These are the intended values for this situation. Other values can still be useful in
              real life.
            </p>

            <button
              type="button"
              className="coc-fit-btn"
              style={btnStyle}
              onClick={handleNext}
            >
              Got it
            </button>
          </div>
        </div>
      ) : null}

      {rulesOpen ? (
        <YogaThemedModal
          open
          title="How to play"
          titleId="coc-rules-title"
          themeData={themeData}
          matchButtonColors
          confirmText="Got it"
          onConfirm={() => setRulesOpen(false)}
        >
          <ul className="cg-rules-list">
            {playRules.map((rule, i) => (
              <li key={i}>{rule}</li>
            ))}
          </ul>
        </YogaThemedModal>
      ) : null}

      {alertModal ? (
        <YogaThemedModal
          open
          title={alertModal.title}
          titleId="coc-alert-title"
          themeData={themeData}
          matchButtonColors
          confirmText={alertModal.confirmText || "OK"}
          onConfirm={alertModal.onConfirm || closeAlert}
        >
          <p>{alertModal.message}</p>
        </YogaThemedModal>
      ) : null}
    </div>
  );
}
